import React, { useState } from 'react';
import dayjs from 'dayjs';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import { ShareIcon } from 'lucide-react';

import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useNotifications } from 'src/notifications';
import { LeadUtils } from 'src/utils';
import { copyToClipboard } from 'src/components/copy-text-button/utils';
import { formatLeadShareText } from 'src/components/lead-sidebar/format-lead-share-text';
import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';
import { CopyIcon } from 'src/icons';

import styles from 'src/components/lead-sidebar/ShareLeadButton.module.css';

type Props = {
    lead: ILead;
    showings: IShowing[];
    propertyName?: string;
    /** The smart fields shown in the Lead Info section, so the copy matches the UI. */
    validSmartFields: [string, string | Record<string, string>][];
};

export default function ShareLeadButton({ lead, showings, propertyName, validSmartFields }: Props) {
    const { userSettings } = useAuthenticatedState();
    const { addNotification } = useNotifications();

    const [anchorEl, setAnchorEl] = useState<HTMLElement>();

    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        analyticsClient.track(AnalyticsEvent.CLICKED_SHARE_LEAD, { lead_uuid: lead.uuid });
        setAnchorEl(event.currentTarget);
    };

    const handleCopyToClipboard = async () => {
        const upcomingShowing = showings.find((showing) => {
            return showing.lead_uuid === lead.uuid &&
                !dayjs(showing.start_time).isBefore(dayjs());
        });

        const text = formatLeadShareText({
            lead,
            leadName: LeadUtils.getLeadName(lead),
            validSmartFields,
            propertyName,
            upcomingShowing,
            timezoneOverride: userSettings.timezone_override,
        });

        const isSuccess = await copyToClipboard(text);
        analyticsClient.track(AnalyticsEvent.COPIED_LEAD_TO_CLIPBOARD, {
            lead_uuid: lead.uuid,
            success: isSuccess,
        });

        if (isSuccess) {
            addNotification('Lead details copied to clipboard', 'success');
        } else {
            addNotification('Failed to copy lead details', 'error');
        }

        setAnchorEl(undefined);
    };

    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                fullWidth
                className={styles.shareBtn}
                onClick={handleOpenMenu}
                startIcon={<ShareIcon size={16} />}
                data-testid="share-lead-btn"
            >
                Share
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(undefined)}
            >
                <MenuItem
                    onClick={handleCopyToClipboard}
                    data-testid="share-lead-copy-to-clipboard"
                >
                    <ListItemIcon>
                        <CopyIcon />
                    </ListItemIcon>
                    <Typography variant="inherit">
                        Copy to Clipboard
                    </Typography>
                </MenuItem>
            </Menu>
        </>
    );
}
